'use client'
import {useCallback, useState} from 'react'

import {buttonClasses} from '@/app/[locale]/(website)/components/ui/ButtonClasses'

interface NewsletterFormProps {
    buttonText?: string
    placeholderText?: string
    successMessage?: string
    errorMessageTooManySignups?: string
    errorMessageFetchFailed?: string
    submitUrl: string
    waitMessage: string
}

export default function NewsletterForm({
    buttonText = 'Sign up',
    placeholderText = 'you@example.com',
    successMessage = "Thanks! We'll be in touch!",
    errorMessageTooManySignups = 'Too many signups, please try again in a little while',
    errorMessageFetchFailed = 'Failed to fetch. Please try again later.',
    submitUrl,
    waitMessage,
}: NewsletterFormProps) {
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [email, setEmail] = useState('')

    // useCallback to memoize the function and prevent re-creation on each render
    const submitHandler = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()
            setLoading(true)
            setError(null)

            const timestamp = Date.now()
            const previousTimestamp = localStorage.getItem('loops-form-timestamp')

            if (previousTimestamp && Number(previousTimestamp) + 60000 > timestamp) {
                setError(errorMessageTooManySignups)
                setLoading(false)
                return
            }
            localStorage.setItem('loops-form-timestamp', timestamp.toString())

            try {
                const response = await fetch(submitUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: `userGroup=&mailingLists=&email=${encodeURIComponent(email)}`,
                })

                if (response.ok) {
                    setSuccess(true)
                    setEmail('')
                } else {
                    const data = await response.json()
                    setError(data.message || response.statusText)
                }
            } catch (fetchError: any) {
                if (fetchError.message === 'Failed to fetch') {
                    setError(errorMessageFetchFailed)
                } else {
                    setError(fetchError.message)
                }

                localStorage.setItem('loops-form-timestamp', '')
            } finally {
                setLoading(false)
            }
        },
        [email, errorMessageTooManySignups, errorMessageFetchFailed, submitUrl],
    )

    // Handle email change outside of JSX as well
    const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    }, [])

    return (
        <div className="flex justify-start w-full flex-col gap-3">
            {success ? (
                <div className="newsletter-success">
                    <p className="text-text-tertiary">{successMessage}</p>
                </div>
            ) : (
                <form
                    className="newsletter-form w-full"
                    onSubmit={submitHandler}
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                    }}
                >
                    <input
                        className="lg:w-full text-text-primary bg-bg-tertiary text-sm mr-2 border border-border-secondary rounded-lg px-3 py-2"
                        name="newsletter-form-input"
                        type="email"
                        placeholder={placeholderText}
                        required
                        value={email}
                        onChange={handleEmailChange}
                    />
                    <button
                        type="submit"
                        className={buttonClasses({
                            variant: 'vibrant',
                        })}
                        style={
                            loading
                                ? {display: 'none'}
                                : {
                                      fontSize: '14px',
                                      color: '#fff',
                                      padding: '9px 17px',
                                      borderRadius: '6px',
                                  }
                        }
                    >
                        {buttonText}
                    </button>
                    {loading && (
                        <button
                            type="button"
                            className={buttonClasses({
                                variant: 'vibrant',
                            })}
                            style={{
                                background: '#e65300',
                                fontSize: '14px',
                                color: '#fff',
                                padding: '9px 17px',
                                borderRadius: '6px',
                            }}
                        >
                            {waitMessage}
                        </button>
                    )}
                </form>
            )}

            {error && (
                <div className="newsletter-error" style={{color: '#b91c1c'}}>
                    <p className="text-text-tertiary">{error}</p>
                </div>
            )}
        </div>
    )
}
